class VideoHeroMedia extends HTMLElement {
  #scrollTrigger = null;
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;

    this.video = this.querySelector('.video-hero__video');
    this.media = this.querySelector('.video-hero__media');
    if (!this.video || !this.media) return;

    this.#initialized = true;

    this.#setupScrollExpand();
  }

  disconnectedCallback() {
    this.#scrollTrigger?.kill();
  }

  #setupScrollExpand() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.media.style.transform = 'none';
      this.#playMuted();
      return;
    }

    if (window.gsap && window.ScrollTrigger) {
      this.#initScrollExpand();
      return;
    }

    window.addEventListener(
      'load',
      () => {
        if (window.gsap && window.ScrollTrigger) {
          this.#initScrollExpand();
        } else {
          this.media.style.transform = 'none';
          this.#playMuted();
        }
      },
      { once: true }
    );
  }

  #initScrollExpand() {
    window.gsap.registerPlugin(window.ScrollTrigger);

    const style = getComputedStyle(this);
    const maxContainerWidth = parseFloat(style.getPropertyValue('--video-hero-max-width')) || 1360;
    const mobileGutter = parseFloat(style.getPropertyValue('--video-hero-gutter')) || 20;
    // Lifts the CSS max-width cap, so offsetWidth below is the uncapped media width.
    window.gsap.set(this.media, { maxWidth: '100%', transformOrigin: '50% 0%' });
    const initialScale = () => {
      // Mobile mirrors the viewport-based CSS preload transform in video-hero.liquid; keep them in sync.
      if (window.matchMedia('(max-width: 749px)').matches) {
        return Math.max(1, window.innerWidth - mobileGutter * 2) / window.innerWidth;
      }
      const mediaWidth = this.media.offsetWidth;
      return Math.min(maxContainerWidth, mediaWidth) / mediaWidth;
    };

    const controls = this.querySelectorAll('.video-player__controls');
    const playIcons = this.querySelectorAll('.video-player__icon');
    if (controls.length) window.gsap.set(controls, { transformOrigin: 'left bottom' });
    const counterScale = () => 1 / initialScale();

    const tween = window.gsap.timeline();
    tween.fromTo(this.media, { scale: initialScale }, { scale: 1, ease: 'none' }, 0);
    if (controls.length) tween.fromTo(controls, { scale: counterScale }, { scale: 1, ease: 'none' }, 0);
    if (playIcons.length) tween.fromTo(playIcons, { scale: counterScale }, { scale: 1, ease: 'none' }, 0);

    this.#scrollTrigger = window.ScrollTrigger.create({
      trigger: this,
      start: 'top center',
      end: 'center center',
      scrub: 0.3,
      invalidateOnRefresh: true,
      animation: tween,
      onEnter: () => this.#playMuted(),
      onEnterBack: () => this.#playMuted(),
    });
  }

  #playMuted() {
    const players = this.querySelectorAll('video-player-media');
    const visible = [...players].find(p => p.offsetParent !== null) ?? players[0];
    visible?.playMuted();
  }
}

if (!customElements.get('video-hero-media')) {
  customElements.define('video-hero-media', VideoHeroMedia);
}
